import { Wrench } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { useState, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import axios from "@/utils/axios"

export default function Maintenance() {
  const navigate = useNavigate()
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const response = await axios.get("/maintenance-status")
      const maintenanceMode = response.data?.maintenanceMode ?? false
      setIsMaintenanceMode(maintenanceMode)
      setError(null)
      
      // If maintenance mode is disabled, redirect to home
      if (!maintenanceMode) {
        navigate("/")
      }
    } catch (err) {
      // If we can't check status, assume maintenance mode is enabled
      // This prevents users from accessing the site if the API is down
      console.error("Error checking maintenance status:", err)
      setError("Unable to check maintenance status")
      setIsMaintenanceMode(true)
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    // Check maintenance status on mount
    checkMaintenanceStatus()

    // Poll every 30 seconds to check if maintenance mode is still enabled
    const interval = setInterval(() => {
      checkMaintenanceStatus()
    }, 30000)

    return () => clearInterval(interval)
  }, [checkMaintenanceStatus])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <Wrench className="h-16 w-16 text-primary mb-4 mx-auto animate-spin" />
          <p className="text-lg text-gray-600">Checking maintenance status...</p>
        </div>
      </div>
    )
  }

  if (!isMaintenanceMode) {
    // Maintenance mode is disabled, component will redirect via useEffect
    return null
  }

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 bg-cover bg-center"
      style={{ backgroundImage: "url(/placeholder.svg?height=1080&width=1920)" }}
    >
      <div className="absolute inset-0 bg-black opacity-60"></div> {/* Overlay for readability */}
      <div className="relative max-w-md w-full space-y-8 text-center rounded-lg">
        <div className="flex flex-col items-center justify-center">
          <Wrench className="h-16 w-16 text-primary mb-4" />
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">Under Maintenance</h1>
          <p className="mt-4 text-lg text-gray-600">
            We're currently performing essential maintenance to improve your experience. We'll be back online shortly!
          </p>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        <div className="mt-6">
          <p className="text-sm text-gray-500">
            We're checking every 30 seconds for updates. This page will automatically refresh when maintenance is complete.
          </p>
        </div>

        <div className="mt-8">
          <Link to="/">
            <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors">
              Try Again
            </Button>
          </Link>
        </div>
        <div className="mt-6">
          <p className="text-sm text-gray-500">Thank you for your patience.</p>
        </div>
      </div>
    </div>
  )
}
